import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, UserPlus, X, AlertCircle } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

const STAMPA_EXTRA = 5;

export default function NewOrder() {
  const { user } = useAuth();
  const puoCreare = puoFare(user, 'ordini', 'create');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [items, setItems] = useState([{ product_id:'', variant_id:'', quantita:1, note:'', stampa_nome:false, nome_stampa:'' }]);
  const [noteOrdine, setNoteOrdine] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ nome:'', cognome:'', email:'', telefono:'' });
  const [savingCust, setSavingCust] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    const [c, p] = await Promise.all([api.get('/customers'), api.get('/products')]);
    setCustomers(c.data);
    setProducts(p.data);
  };
  useEffect(() => { loadData(); }, []);

  const getProduct = (pid) => products.find(p => p.id === parseInt(pid));
  const getVariants = (pid) => getProduct(pid)?.variants?.filter(v => v.attiva) || [];

  const getPrezzo = (item) => {
    const prod = getProduct(item.product_id);
    if (!prod || !item.variant_id) return 0;
    return prod.prezzo_vendita_ivato + (item.stampa_nome ? STAMPA_EXTRA : 0);
  };

  const totale = items.reduce((s, item) => s + getPrezzo(item) * Math.max(parseInt(item.quantita) || 0, 0), 0);

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: val };
      if (field === 'product_id') { updated.variant_id = ''; updated.stampa_nome = false; updated.nome_stampa = ''; }
      return updated;
    }));
  };

  const addItem = () => setItems(prev => [...prev, { product_id:'', variant_id:'', quantita:1, note:'', stampa_nome:false, nome_stampa:'' }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleCreateCustomer = async () => {
    if (!newCust.nome || !newCust.cognome) { alert('Nome e cognome obbligatori'); return; }
    setSavingCust(true);
    try {
      const res = await api.post('/customers', newCust);
      await loadData();
      setCustomerId(String(res.data.id));
      setShowNewCustomer(false);
      setNewCust({ nome:'', cognome:'', email:'', telefono:'' });
    } catch (err) {
      alert(err.response?.data?.error || 'Errore creazione cliente');
    } finally { setSavingCust(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!customerId) { setError('Seleziona un cliente'); return; }
    if (items.length === 0) { setError('Aggiungi almeno un articolo'); return; }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.product_id) { setError(`Articolo ${i+1}: seleziona un prodotto`); return; }
      if (!item.variant_id) { setError(`Articolo ${i+1}: seleziona la taglia`); return; }
      if (!item.quantita || parseInt(item.quantita) < 1) { setError(`Articolo ${i+1}: quantità non valida`); return; }
      if (item.stampa_nome && !item.nome_stampa.trim()) { setError(`Articolo ${i+1}: inserisci il nome da stampare`); return; }
    }

    setLoading(true);
    try {
      const payload = {
        customer_id: parseInt(customerId),
        note: noteOrdine || null,
        items: items.map(item => ({
          variant_id: parseInt(item.variant_id),
          quantita: parseInt(item.quantita),
          note: [
            item.stampa_nome ? `STAMPA NOME: ${item.nome_stampa}` : '',
            item.note || '',
          ].filter(Boolean).join(' | ') || null,
        })),
      };
      await api.post('/orders', payload);
      navigate('/orders');
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante la creazione dell\'ordine');
      setLoading(false);
    }
  };

  if (!puoCreare) {
    return (
      <div style={{ padding:'40px', textAlign:'center', color:'var(--text-dim)' }}>
        Non hai il permesso per creare ordini.
        <div style={{ marginTop:'12px' }}>
          <button style={{ background:'var(--border)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 14px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' }} onClick={() => navigate('/orders')}>TORNA AGLI ORDINI</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth:'900px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'28px' }}>
        <button type="button" style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--text-dim)', padding:'4px' }} onClick={() => navigate('/orders')}>
          <ArrowLeft size={20}/>
        </button>
        <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' }}>NUOVO ORDINE</h1>
      </div>

      {error && (
        <div style={s.errorBanner}>
          <AlertCircle size={15} color="#EF4444" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

        {/* ── CLIENTE ── */}
        <div style={s.card}>
          <div style={s.cardTitle}>CLIENTE</div>
          <div style={{ display:'flex', gap:'10px' }}>
            <select style={{ ...s.input, flex:1 }} value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">— seleziona cliente —</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.cognome} {c.nome}{c.email ? ` · ${c.email}` : ''}</option>
              ))}
            </select>
            <button type="button" style={s.btnBlue} onClick={() => setShowNewCustomer(v => !v)}>
              <UserPlus size={14}/> NUOVO
            </button>
          </div>

          {showNewCustomer && (
            <div style={s.inlineBox}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                <span style={{ fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'var(--text-muted)' }}>CREA NUOVO CLIENTE</span>
                <button type="button" style={{ background:'transparent', border:'none', color:'var(--text-dim)', cursor:'pointer' }} onClick={() => setShowNewCustomer(false)}><X size={14}/></button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                {[['cognome','COGNOME *'],['nome','NOME *'],['email','EMAIL'],['telefono','TELEFONO']].map(([k, l]) => (
                  <div key={k} style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    <label style={s.label}>{l}</label>
                    <input style={s.inputSm} value={newCust[k]} onChange={e => setNewCust(f => ({...f,[k]:e.target.value}))} />
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" style={s.btnSec} onClick={() => setShowNewCustomer(false)}>ANNULLA</button>
                <button type="button" style={s.btnPri} onClick={handleCreateCustomer} disabled={savingCust}>
                  {savingCust ? 'SALVATAGGIO...' : 'CREA E SELEZIONA'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── ARTICOLI ── */}
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <div style={s.cardTitle}>ARTICOLI ({items.length})</div>
            <button type="button" style={s.btnSec} onClick={addItem}><Plus size={13}/> AGGIUNGI ARTICOLO</button>
          </div>

          {items.map((item, idx) => {
            const prod = getProduct(item.product_id);
            const variants = getVariants(item.product_id);
            const prezzo = getPrezzo(item);
            const rigaTotale = prezzo * (parseInt(item.quantita) || 0);

            return (
              <div key={idx} style={s.itemCard}>
                {/* Riga 1: prodotto + taglia + qtà + prezzo + elimina */}
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 70px 110px 36px', gap:'10px', alignItems:'end', marginBottom:'10px' }}>
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    <label style={s.label}>PRODOTTO</label>
                    <select style={s.input} value={item.product_id} onChange={e => updateItem(idx,'product_id',e.target.value)}>
                      <option value="">— seleziona prodotto —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    <label style={s.label}>TAGLIA</label>
                    <select style={s.input} value={item.variant_id} onChange={e => updateItem(idx,'variant_id',e.target.value)} disabled={!item.product_id}>
                      <option value="">— taglia —</option>
                      {variants.map(v => (
                        <option key={v.id} value={v.id}>{v.taglia} (disp: {v.inventory?.quantita_disponibile || 0})</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    <label style={s.label}>QTÀ</label>
                    <input style={s.input} type="number" min="1" value={item.quantita} onChange={e => updateItem(idx,'quantita',e.target.value)} />
                  </div>
                  <div style={{ textAlign:'right', paddingBottom:'2px' }}>
                    {item.variant_id && (
                      <>
                        <div style={{ fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-xl)', fontWeight:'800' }}>€{rigaTotale.toFixed(2)}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color:'var(--text-dim)' }}>€{prezzo.toFixed(2)} cad.</div>
                      </>
                    )}
                  </div>
                  <button type="button" style={s.deleteBtn} onClick={() => removeItem(idx)}>
                    <X size={13}/>
                  </button>
                </div>

                {/* Riga 2: stampa nome (solo se prodotto lo prevede) + note */}
                <div style={{ display:'grid', gridTemplateColumns: (prod?.personalizzabile && item.stampa_nome) ? '200px 1fr 1fr' : prod?.personalizzabile ? '200px 1fr' : '1fr', gap:'10px', alignItems:'end' }}>
                  {/* STAMPA NOME — solo se il prodotto ha il flag abilitato */}
                  {prod?.personalizzabile && (
                    <div style={{ ...s.stampaNomeBox, ...(item.stampa_nome ? s.stampaNomeBoxOn : {}) }}>
                      <input type="checkbox" id={`stampa-${idx}`} checked={item.stampa_nome}
                        onChange={e => updateItem(idx,'stampa_nome',e.target.checked)}
                        style={{ accentColor:'#F4600C', width:'15px', height:'15px', cursor:'pointer' }} />
                      <label htmlFor={`stampa-${idx}`} style={{ cursor:'pointer', fontSize:'var(--font-size-xs)', fontWeight:'700', color: item.stampa_nome ? '#F4600C' : 'var(--text-dim)' }}>
                        STAMPA NOME +€{STAMPA_EXTRA}
                      </label>
                    </div>
                  )}

                  {/* Campo nome — solo se stamp checkbox ON */}
                  {prod?.personalizzabile && item.stampa_nome && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                      <label style={s.label}>NOME DA STAMPARE *</label>
                      <input style={{ ...s.input, borderColor: !item.nome_stampa ? '#EF444460' : 'var(--border2)' }}
                        value={item.nome_stampa}
                        onChange={e => updateItem(idx,'nome_stampa',e.target.value)}
                        placeholder="es. Marco Rossi" />
                    </div>
                  )}

                  {/* NOTE — sempre visibili */}
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                    <label style={s.label}>NOTE ARTICOLO</label>
                    <input style={s.input} value={item.note} onChange={e => updateItem(idx,'note',e.target.value)} placeholder="Note opzionali..." />
                  </div>
                </div>
              </div>
            );
          })}

          {items.length > 0 && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'14px', borderTop:'1px solid var(--border)', marginTop:'4px' }}>
              <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)' }}>{items.length} articolo{items.length !== 1 ? 'i' : ''}</div>
              <div style={{ textAlign:'right' }}>
                <div style={{ color:'var(--text-dim)', fontSize: 'var(--font-size-xs)', letterSpacing:'1.5px', fontWeight:'700', marginBottom:'2px' }}>TOTALE ORDINE</div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', color:'#F4600C' }}>€{totale.toFixed(2)}</div>
              </div>
            </div>
          )}
        </div>

        {/* ── NOTE ORDINE ── */}
        <div style={s.card}>
          <div style={s.cardTitle}>NOTE ORDINE</div>
          <textarea style={{ ...s.input, resize:'vertical', minHeight:'70px' }} value={noteOrdine}
            onChange={e => setNoteOrdine(e.target.value)} placeholder="Note generali sull'ordine..." />
        </div>

        <div style={{ display:'flex', gap:'12px', justifyContent:'flex-end' }}>
          <button type="button" style={s.btnSec} onClick={() => navigate('/orders')}>ANNULLA</button>
          <button type="submit" style={{ ...s.btnPri, padding:'12px 32px', fontSize:'var(--font-size-sm)' }} disabled={loading}>
            {loading ? 'CREAZIONE IN CORSO...' : 'CREA ORDINE'}
          </button>
        </div>
      </form>
    </div>
  );
}

const s = {
  card: { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'22px' },
  cardTitle: { fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-sm)', fontWeight:'700', letterSpacing:'2px', color:'var(--text-dim)', marginBottom:'14px' },
  label: { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'var(--text-dim)' },
  input: { background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 12px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  inputSm: { background:'var(--border)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'7px 10px', color:'var(--text)', fontSize:'var(--font-size-xs)', outline:'none', width:'100%', boxSizing:'border-box' },
  btnPri: { background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 18px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' },
  btnSec: { background:'var(--border)', color:'var(--text)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 14px', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' },
  btnBlue: { background:'#3B82F620', border:'1px solid #3B82F640', borderRadius:'8px', padding:'9px 14px', color:'#3B82F6', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', whiteSpace:'nowrap' },
  inlineBox: { background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'10px', padding:'16px', marginTop:'12px' },
  itemCard: { background:'var(--bg-input)', border:'1px solid #222', borderRadius:'10px', padding:'14px 16px', marginBottom:'10px' },
  stampaNomeBox: { display:'flex', alignItems:'center', gap:'8px', background:'var(--border)', border:'1px solid var(--border2)', borderRadius:'8px', padding:'9px 14px' },
  stampaNomeBoxOn: { background:'#F4600C12', border:'1px solid #F4600C40' },
  deleteBtn: { background:'#EF444415', border:'1px solid #EF444430', borderRadius:'6px', padding:'8px', color:'#EF4444', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', height:'38px', width:'36px' },
  errorBanner: { display:'flex', alignItems:'center', gap:'10px', background:'#EF444415', border:'1px solid #EF444440', borderRadius:'10px', padding:'12px 16px', marginBottom:'16px', color:'#EF4444', fontSize:'var(--font-size-sm)', fontWeight:'600' },
};
